import { motion } from "framer-motion";
import logo from "../assets/logo/pms-logo.png";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay },
});

const stats = [
  { value: "15+", label: "Years Experience" },
  { value: "500+", label: "Clients Served" },
  { value: "1M+", label: "Units Delivered" },
];

export default function About() {
  return (
    <section id="about" className="py-28 bg-light overflow-hidden">
      <div className="max-w-6xl mx-auto px-6">

        {/* Top row: logo block + main text */}
        <div className="grid md:grid-cols-2 gap-16 items-start mb-20">

          {/* LEFT — Logo card + stats */}
          <motion.div {...fadeUp(0)} className="flex flex-col gap-8">

            {/* Logo display card */}
            <div className="relative bg-white rounded-2xl shadow-lg p-10 flex items-center justify-center border border-gray-100 overflow-hidden">
              {/* Decorative green corner accent */}
              <div className="absolute top-0 left-0 w-24 h-24 bg-brand-green/8 rounded-br-[80px]" />
              <div className="absolute bottom-0 right-0 w-16 h-16 bg-brand-lightGreen/10 rounded-tl-[60px]" />

              <img
                src={logo}
                alt="PMS Packages"
                className="h-20 w-auto object-contain relative z-10"
              />
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4">
              {stats.map(({ value, label }, i) => (
                <motion.div
                  key={label}
                  {...fadeUp(0.15 + i * 0.1)}
                  className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100"
                >
                  <p className="font-heading text-2xl font-bold text-brand-green">{value}</p>
                  <p className="text-xs text-gray-500 mt-1 leading-tight">{label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* RIGHT — Heading + description */}
          <div className="flex flex-col justify-center">
            <motion.div {...fadeUp(0.1)} className="flex items-center gap-3 mb-4">
              <span className="block w-8 h-px bg-brand-green" />
              <span className="text-brand-green text-xs uppercase tracking-[0.2em] font-semibold">
                Who We Are
              </span>
            </motion.div>

            <motion.h2 {...fadeUp(0.2)} className="font-heading text-4xl md:text-5xl leading-tight mb-6">
              Built on Quality,<br />
              <span className="text-brand-green">Driven by Results</span>
            </motion.h2>

            <motion.p {...fadeUp(0.3)} className="text-gray-600 text-lg leading-relaxed">
              PMS Packages is a full-service packaging manufacturer specializing in
              high-quality corrugated solutions for businesses across industries.
              We help brands grow through durable, cost-effective, and custom
              packaging engineered to perform throughout the supply chain.
            </motion.p>
          </div>
        </div>

        {/* Bottom row: Mission + Vision cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {[
            
            {
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              ),
              title: "Our Vision",
              text: "To be a trusted packaging partner by investing in modern technology, skilled people, and consistent quality.",
            },
            {
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1M4.22 4.22l.7.7m13.16 13.16.7.7M3 12H2m20 0h-1M4.22 19.78l.7-.7M18.36 5.64l.7-.7M12 7a5 5 0 100 10A5 5 0 0012 7z" />
                </svg>
              ),
              title: "Our Mission",
              text: "To empower businesses with reliable and cost-effective packaging solutions that support growth and brand confidence.",
            },
          ].map(({ icon, title, text }, i) => (
            <motion.div
              key={title}
              {...fadeUp(0.2 + i * 0.12)}
              className="group bg-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-md hover:border-brand-green/20 transition-all duration-300"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center group-hover:bg-brand-green group-hover:text-white transition-all duration-300">
                  {icon}
                </div>
                <h4 className="font-heading text-xl font-semibold">{title}</h4>
              </div>
              <p className="text-gray-600 leading-relaxed">{text}</p>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}